import { ActionTree, GetterTree, MutationTree } from "vuex";

import { InboundCallServiceClient } from "@/serviceClients/InboundCallServiceClient";

import {
  isKokattoErrorResponse,
  KokattoErrorResponse,
} from "@/serviceClients/responses/KokattoErrorResponse";

import {
  State,
  AsyncState,
  RequestCallState,
  RequestCallData,
  GetRequestCallPayload,
} from "./types";
import { GetRequestCallPermissionResponse } from "@/serviceClients/responses/InboundCallResponse";
import { RequestPostCallPermission } from "@/serviceClients/requests/InboundCallRequest";

const DEFAULT_ASYNC_STATE: AsyncState = {
  loading: false,
  loadingForm: false,
  data: null,
  error: null,
};

const DEFAULT_STATE: State = {
  requestCall: { ...DEFAULT_ASYNC_STATE }, // retrieve the request call using this state
  allowCsInitiatedCall: false,
};

const state: State = { ...DEFAULT_STATE };

const getters: GetterTree<State, any> = {
  getRequestCallState: (state: State): RequestCallState => state.requestCall,
  getAllowCsInitiatedCall: (state: State): boolean =>
    state.allowCsInitiatedCall,
};

const mutations: MutationTree<State> = {
  setRequestCallData(state: State, data: RequestCallData) {
    state.requestCall.data = data;
  },
  setLoading(state: State, loading: boolean) {
    state.requestCall.loading = loading;
  },
  setLoadingForm(state: State, loading: boolean) {
    state.requestCall.loadingForm = loading;
  },
  setError(state: State, error: Error) {
    state.requestCall.error = error;
  },
  setAllowCsInitiatedCall(state: State, allowCsInitiatedCall: boolean) {
    state.allowCsInitiatedCall = allowCsInitiatedCall;
  },
};

const actions: ActionTree<State, any> = {
  async getRequestCallPermission(
    { commit, rootGetters },
    payload: GetRequestCallPayload
  ): Promise<void> {
    try {
      commit("setLoading", true);
      const inboundCallServiceClient = new InboundCallServiceClient({
        token: rootGetters.getKokattoTokenAccess,
      });

      const requestCallData:
        | GetRequestCallPermissionResponse
        | KokattoErrorResponse = await inboundCallServiceClient.getCallPermissionUser(
        payload
      );

      commit("setRequestCallData", requestCallData);
    } catch (error) {
      console.error(
        "[ERROR] Something went wrong in getCallPermission()",
        error
      );
      commit("setError", error);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async postRequestCallPermission(
    { dispatch, commit, rootGetters },
    payload: RequestPostCallPermission
  ) {
    try {
      commit("setLoadingForm", true);
      const inboundCallServiceClient = new InboundCallServiceClient({
        token: rootGetters.getKokattoTokenAccess,
      });
      await inboundCallServiceClient.postCallPermissionUser(payload);
    } catch (error) {
      console.error(
        "[ERROR] Something went wrong in postRequestCallPermission()",
        error
      );
      commit("setError", error);
      throw error;
    } finally {
      await dispatch("getRequestCallPermission", {
        phoneNumber: payload.phoneNumber,
      });
      commit("setLoadingForm", false);
    }
  },
  async getCallCenterConfig({ commit, rootGetters }) {
    try {
      const inboundCallServiceClient = new InboundCallServiceClient({
        token: rootGetters.getKokattoTokenAccess,
      });
      const callCenterConfig =
        await inboundCallServiceClient.getCallCenterConfig();
      if (isKokattoErrorResponse(callCenterConfig)) {
        throw new Error(callCenterConfig.message);
      }
      commit(
        "setAllowCsInitiatedCall",
        Boolean(callCenterConfig.data?.allowCSInitiatedCall)
      );
    } catch (error) {
      console.error(
        "[ERROR] Something went wrong in getCallCenterConfig()",
        error
      );
      commit("setAllowCsInitiatedCall", false);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
